export type AssuranceLevel = 'aal1' | 'aal2' | null;
export type TotpFactor = { id: string; status: 'verified' | 'unverified' | string; factor_type?: string; friendly_name?: string };

type MfaClient = {
  auth: {
    mfa: {
      getAuthenticatorAssuranceLevel: () => Promise<{ data: { currentLevel: AssuranceLevel; nextLevel: AssuranceLevel } | null; error: { message: string } | null }>;
      listFactors: () => Promise<{ data: { all: TotpFactor[]; totp: TotpFactor[] } | null; error: { message: string } | null }>;
      challenge: (input: { factorId: string }) => Promise<{ data: { id: string } | null; error: { message: string } | null }>;
      verify: (input: { factorId: string; challengeId: string; code: string }) => Promise<{ error: { message: string } | null }>;
      unenroll: (input: { factorId: string }) => Promise<{ error: { message: string } | null }>;
      enroll: (input: { factorType: 'totp'; friendlyName: string }) => Promise<{ data: { id: string; totp: { qr_code: string; secret: string } } | null; error: { message: string } | null }>;
    };
    refreshSession: () => Promise<unknown>;
  };
};

export type MfaSnapshot = {
  currentLevel: AssuranceLevel;
  nextLevel: AssuranceLevel;
  verifiedFactors: TotpFactor[];
  unverifiedFactors: TotpFactor[];
};

export async function getMfaSnapshot(client: MfaClient): Promise<MfaSnapshot> {
  const [assurance, factors] = await Promise.all([
    client.auth.mfa.getAuthenticatorAssuranceLevel(),
    client.auth.mfa.listFactors(),
  ]);
  if (assurance.error) throw new Error(assurance.error.message);
  if (factors.error) throw new Error(factors.error.message);
  const all = factors.data?.all ?? factors.data?.totp ?? [];
  return {
    currentLevel: assurance.data?.currentLevel ?? null,
    nextLevel: assurance.data?.nextLevel ?? null,
    verifiedFactors: all.filter((factor) => factor.status === 'verified'),
    unverifiedFactors: all.filter((factor) => factor.status !== 'verified'),
  };
}

export function requiresMfaChallenge(snapshot: MfaSnapshot) {
  return snapshot.currentLevel === 'aal1' && snapshot.nextLevel === 'aal2' && snapshot.verifiedFactors.length > 0;
}

export function protectedAdminDestination(snapshot: MfaSnapshot) {
  if (requiresMfaChallenge(snapshot)) return '/mfa-challenge';
  if (snapshot.verifiedFactors.length === 0) return '/security';
  return snapshot.currentLevel === 'aal2' ? '/dashboard' : '/mfa-challenge';
}

export async function verifyTotpCode(client: MfaClient, factorId: string, code: string) {
  if (!factorId) throw new Error('No verified authenticator was found.');
  if (!/^\d{6}$/.test(code)) throw new Error('Enter the six-digit code from your authenticator app.');
  const challenge = await client.auth.mfa.challenge({ factorId });
  if (challenge.error || !challenge.data) throw new Error(challenge.error?.message ?? 'The MFA challenge could not be created.');
  const verification = await client.auth.mfa.verify({ factorId, challengeId: challenge.data.id, code });
  if (verification.error) throw new Error(verification.error.message);
  await client.auth.refreshSession();
  const snapshot = await getMfaSnapshot(client);
  if (snapshot.currentLevel !== 'aal2') throw new Error('MFA was verified, but the session did not reach AAL2. Sign out and try again.');
  return snapshot;
}

export async function beginTotpEnrollment(client: MfaClient) {
  const snapshot = await getMfaSnapshot(client);
  for (const factor of snapshot.unverifiedFactors) {
    const result = await client.auth.mfa.unenroll({ factorId: factor.id });
    if (result.error) throw new Error('An incomplete MFA enrollment already exists. Sign out and try again.');
  }
  const result = await client.auth.mfa.enroll({ factorType: 'totp', friendlyName: 'Aden Eats Admin' });
  if (result.error || !result.data) throw new Error(result.error?.message ?? 'MFA enrollment could not be started.');
  return result.data;
}
